<template>
  <div class="c-price">
    <div v-if="!csvData || csvData.length === 0">
      No data available. Please add CSV data to CIS-Pricing-Selector data source.
    </div>

    <!-- Mobile -->
    <div class="price-mobile">
      <div
        v-for="(option, index) in csvDataHeader"
        :key="'csvDataHeader' + index"
        v-html="option"
        :class="`headline order${hasType ? index + 1 : index + 2}`"
      ></div>
      <div v-if="hasType && csvData && csvData.length > 0" class="c-price__select-div order1">
        <select v-model="selectedType" @change="onTypeChange($event)" class="c-price__select range">
          <option
            v-for="(option, index) in typeSelect"
            :key="'typeSelect' + index"
            :value="option[typeColumn]"
          >
            {{ option[typeColumn] }}
          </option>
        </select>
      </div>
      <div v-if="csvData && csvData.length > 0" class="c-price__select-div order2">
        <select
          v-model="selectedRange"
          @change="onChange($event)"
          class="c-price__select range"
          :disabled="
            (hasType && selectedType === 'Select Type') ||
            (hasType && selectedType === 'US Public') ||
            (hasType && selectedType === 'US SLTTs')
          "
        >
          <option
            v-for="(option, index) in csvData"
            :key="index"
            :value="option[headerFirstColumn]"
          >
            {{ option[headerFirstColumn] }}
          </option>
        </select>
      </div>
      <div
        v-for="(option, index) in selectedValues"
        :key="index"
        v-html="option"
        :class="`value order${index + 3}`"
      ></div>
    </div>

    <!-- Tablet -->
    <div class="price-tablet">
      <div
        v-for="(option, index) in onlySelects"
        :key="'onlySelects' + index"
        class="header"
        :class="`${hasType ? 'half' : ''}`"
      >
        {{ option }}
      </div>

      <div v-if="hasType && csvData && csvData.length > 0" class="half c-price__select-div">
        <select v-model="selectedType" @change="onTypeChange($event)" class="c-price__select range">
          <option v-for="(option, index) in typeSelect" :key="index" :value="option[typeColumn]">
            {{ option[typeColumn] }}
          </option>
        </select>
      </div>
      <div v-if="csvData && csvData.length > 0" class="half c-price__select-div">
        <select
          v-model="selectedRange"
          @change="onChange($event)"
          class="c-price__select range"
          :disabled="
            (hasType && selectedType === 'Select Type') ||
            (hasType && selectedType === 'US Public') ||
            (hasType && selectedType === 'US SLTTs')
          "
        >
          <option
            v-for="(option, index) in csvData"
            :key="index"
            :value="option[headerFirstColumn]"
          >
            {{ option[headerFirstColumn] }}
          </option>
        </select>
      </div>
      <div v-if="onlyNonSelectHeaders" class="headers">
        <div
          v-for="(option, index) in onlyNonSelectHeaders"
          :key="index"
          v-html="option"
          class="header small"
        ></div>
      </div>
      <div v-if="selectedValues">
        <div
          v-for="(option, index) in selectedValues"
          :key="index"
          v-html="option"
          class="value small"
        ></div>
      </div>
    </div>

    <!-- Desktop -->
    <table v-if="csvData && csvData.length > 0">
      <thead>
        <tr class="c-price__headerlines">
          <td
            v-for="(option, index) in csvDataHeader"
            :key="index"
            v-html="option"
            class="headline"
          ></td>
        </tr>
      </thead>
      <tbody>
        <tr class="c-price__values" :class="`${hasType ? 'hasType' : ''}`">
          <td v-if="hasType && csvData && csvData.length > 0">
            <div v-if="hasType && csvData && csvData.length > 0" class="c-price__select-div">
              <select
                v-model="selectedType"
                @change="onTypeChange($event)"
                class="c-price__select range"
              >
                <option
                  v-for="(option, index) in typeSelect"
                  :key="index"
                  :value="option[typeColumn]"
                >
                  {{ option[typeColumn] }}
                </option>
              </select>
            </div>
          </td>
          <td v-if="csvData && csvData.length > 0">
            <div v-if="csvData && csvData.length > 0" class="c-price__select-div">
              <select
                v-model="selectedRange"
                @change="onChange($event)"
                class="c-price__select range"
                :disabled="
                  (hasType && selectedType === 'Select Type') ||
                  (hasType && selectedType === 'US Public') ||
                  (hasType && selectedType === 'US SLTTs')
                "
              >
                <option
                  v-for="(option, index) in csvData"
                  :key="index"
                  :value="option[headerFirstColumn]"
                >
                  {{ option[headerFirstColumn] }}
                </option>
              </select>
            </div>
          </td>
          <td
            v-for="(option, index) in selectedValues"
            :key="index"
            v-html="option"
            class="value"
          ></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { reactive } from 'vue';
import { csvParse } from 'd3-dsv';

export default {
  name: 'CIS-Price-Selector',
  props: {
    fields: {
      type: Object
    }
  },
  data() {
    return {
      csvDataSelect: [],
      selectedType: 'Select Type',
      selectedRange: 'Select Range',
      selectedRow: [],
      selectedValues: []
    };
  },
  setup(props) {
    const priceData = reactive({
      csvData: [],
      csvDataHeader: [],
      typeColumn: null,
      hasType: null,
      typeSelect: [],
      onlySelects: [],
      onlyNonSelectHeaders: [],
      headerFirstColumn: null
    });

    if (props?.fields?.csvData?.value) {
      // Parse data
      const data = csvParse(props.fields.csvData.value);

      priceData.csvData = data;
      if (data !== null) priceData.csvDataHeader = data.columns;

      if (data !== null && data[0]) {
        let header = Object.keys(data[0]);

        priceData.hasType = header[0].includes('TYPE');

        if (priceData.hasType) {
          priceData.typeSelect = priceData.csvData.filter((item) => {
            let itemValue = Object.values(item);
            if (itemValue[0] && itemValue[0].length > 0) return item;
          });

          priceData.typeColumn = header[0];
          priceData.headerFirstColumn = header[1];
        } else {
          priceData.headerFirstColumn = header[0];
        }
      }

      priceData.onlySelects = priceData.csvDataHeader.filter((item, index) => {
        if (priceData.hasType) {
          if (index < 2) return item;
        } else {
          if (index < 1) return item;
        }
      });

      priceData.onlyNonSelectHeaders = priceData.csvDataHeader.filter((item, index) => {
        if (priceData.hasType) {
          if (index > 1) return item;
        } else {
          if (index > 0) return item;
        }
      });
    }

    return priceData;
  },
  mounted() {
    this.onChange('Select Range');
  },
  computed: {
    isEditing() {
      return this.$jss.sitecoreContext().pageEditing;
    }
  },
  methods: {
    containsKey(obj, key) {
      return Object.keys(obj).includes(key);
    },
    onChange(event) {
      let selectValue = '';

      if (event === 'Select Range') {
        selectValue = event;
      } else {
        selectValue = event.target.value;
      }

      if (this.csvData !== null) {
        this.selectedRow = this.csvData.filter((item) => {
          return item[this.headerFirstColumn] === selectValue;
        });
      }

      if (this.selectedRow.length > 0) {
        let SelectedValues = Object.values(this.selectedRow[0]);
        if (this.hasType) {
          this.selectedValues = SelectedValues.splice(2);
        } else {
          this.selectedValues = SelectedValues.splice(1);
        }
      }
    },
    onTypeChange() {
      // Set to first row
      this.selectedRange = 'Select Range';
      this.onChange('Select Range');

      if (this.selectedType === 'US Public' || this.selectedType === 'US SLTTs') {
        for (var i = 0; i < this.selectedValues.length; ++i) {
          this.selectedValues.splice(i, 1, 'Free');
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.c-price {
  &:deep() {
    .bestValue {
      display: block;
    }
    .strike {
      text-decoration: line-through;
    }
  }

  .price-mobile,
  .price-tablet {
    display: none;
  }

  table {
    display: none;

    @media screen and (min-width: 1201px) {
      display: block;
    }

    tr {
      &.c-price__headerlines {
        td {
          vertical-align: bottom;
          padding-bottom: 0.5rem;
          @include font-styles('Arial Narrow', 1.4rem, 600, normal, 1.7rem, 0, $dark-blue);
        }
      }

      &.hasType {
        .c-price__select-div {
          width: 17rem;
        }
      }

      td {
        border: none;
        border-right: solid 1.2rem $white;
        padding: 0;

        &:last-child {
          border-right: none;
        }

        &.value {
          width: 11.7rem;
          background-color: $box-gray;
          text-align: right;
          @include font-styles('Arial', 1.6rem, 500, normal, 2.6rem, 0, $gray);
        }

        .c-price__select-div {
          width: 32rem;
        }
      }
    }
  }

  .c-price__select-div {
    position: relative;

    &:after {
      font-weight: 700;
      font-size: 2rem;
      content: '∟';
      transform: translate(-0.9rem, 0.4rem) rotate(-45deg);
      color: $blue;
      right: 0;
      top: 0;
      position: absolute;
      z-index: 1;
      pointer-events: none;
      background-color: $box-gray;
    }
  }

  .c-price__select {
    width: 100%;
    padding: 1.2rem;
    background-color: $box-gray;
    border: none;
    @include font-styles('Arial', 1.6rem, 500, normal, 2.5rem, 0, $gray);
    appearance: none;
    outline: none;
  }

  .value {
    padding: 1.2rem;
    background-color: $box-gray;
    text-align: right;
    @include font-styles('Arial', 1.6rem, 500, normal, 2.6rem, 0, $gray);
  }

  @media screen and (min-width: 768px) and (max-width: 1200px) {
    .price-tablet {
      display: block;

      .headers {
        margin-top: 2rem;
      }

      .header {
        @include font-styles('Arial Narrow', 1.4rem, 600, normal, 1.7rem, 0, $dark-blue);
        margin-bottom: 1rem;
      }

      .half {
        display: inline-block;
        width: calc(50% - 1.2rem);
        margin-right: 1.2rem;
      }

      .small {
        display: inline-block;
        width: calc(20% - 1.2rem);
        margin-right: 1.2rem;
      }
    }
  }

  @media screen and (max-width: 767.5px) {
    .price-mobile {
      width: 100%;
      display: flex;
      flex-direction: column;
    }

    .headline {
      padding-bottom: 1rem;
      @include font-styles('Arial Narrow', 1.4rem, 600, normal, 1.7rem, 0, $dark-blue);
    }

    .value,
    .c-price__select-div {
      margin-bottom: 2rem;
    }

    &:deep() {
      .bestValue {
        display: inline-block;
        padding-right: 0.3rem;
      }
    }

    .order1 {
      order: 1;
    }
    .order2 {
      order: 2;
    }
    .order3 {
      order: 3;
    }
    .order4 {
      order: 4;
    }
    .order5 {
      order: 5;
    }
    .order6 {
      order: 6;
    }
    .order7 {
      order: 7;
    }
    .order8 {
      order: 8;
    }
    .order9 {
      order: 9;
    }
  }
}
</style>
